import React from 'react';
import { ChevronDown } from 'lucide-react';
import { ThemeColorPalette } from '../../types';
import { DEFAULT_THEME_COLORS, mergeThemeColors } from '../../constants/themeColors';

interface LanguageSelectionPreviewProps {
  appName: string;
  themeColors: ThemeColorPalette;
  onboardBgPreview?: string | null;
}

const LanguageSelectionPreview: React.FC<LanguageSelectionPreviewProps> = ({
  appName,
  themeColors,
  onboardBgPreview,
}) => {
  const mergedColors = mergeThemeColors(themeColors);
  const primaryColor =
    mergedColors.PRIMARY ??
    DEFAULT_THEME_COLORS.PRIMARY ??
    '#4a9b8e';
  const textPrimary = '#ffffff';
  const textSecondary = '#f0f0f0';
  const dropdownBg = 'rgba(255, 255, 255, 0.95)';
  const dropdownText = '#333333';
  const dropdownBorder = 'rgba(255, 255, 255, 0.3)';
  const overlayBg = 'rgba(0, 0, 0, 0.3)';

  return (
    <>
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: onboardBgPreview 
            ? `url(${onboardBgPreview})`
            : 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
        }}
      />
      
      {/* Overlay */}
      <div 
        className="absolute inset-0"
        style={{ backgroundColor: overlayBg }}
      />
      
      {/* Content Container */}
      <div className="relative z-10 h-full flex flex-col justify-end p-6 pb-12">
          {/* Welcome Text */}
          <p 
            className="text-center mb-2 text-lg font-normal leading-tight"
            style={{ 
              color: textPrimary,
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
          >
            Choose your language to begin your journey
          </p>
          
          {/* Form Container */}
          <div className="w-full max-w-sm mx-auto">
            {/* Dropdown Container */}
            <div className="relative mb-1">
              <div className="relative">
                <select 
                  className="w-full rounded-lg px-4 py-3 text-sm appearance-none cursor-pointer backdrop-blur-md transition-all duration-300 focus:outline-none focus:shadow-lg"
                  style={{
                    backgroundColor: dropdownBg,
                    border: `1px solid ${dropdownBorder}`,
                    color: dropdownText,
                    boxShadow: '0 0 0 3px rgba(74, 155, 142, 0.2)'
                  }}
                  defaultValue=""
                >
                  <option value="">Selected Language</option>
                  <option value="en">English</option>
                  <option value="es">Español</option>
                  <option value="fr">Français</option>
                  <option value="de">Deutsch</option>
                  <option value="it">Italiano</option>
                  <option value="pt">Português</option>
                  <option value="ru">Русский</option>
                  <option value="ja">日本語</option>
                  <option value="ko">한국어</option>
                  <option value="zh">中文</option>
                  <option value="ar">العربية</option>
                  <option value="hi">हिन्दी</option>
                </select>
                
                {/* Dropdown Arrow */}
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-3 h-2" style={{ color: dropdownText }} />
                </div>
              </div>
            </div>
            
            {/* Description Text */}
            <p 
              className="text-center mb-7 text-xs font-normal leading-tight opacity-90"
              style={{ 
                color: textSecondary,
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
              }}
            >
              Select your preferred language to unlock unique, self-guided tours tailored for you
            </p>
            
            {/* Continue Button */}
            <button 
              className="w-full rounded-lg py-4.5 px-6 text-xl font-bold text-white cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-0.5 active:transform active:translate-y-0"
              style={{
                backgroundColor: primaryColor,
                boxShadow: '0 4px 12px rgba(74, 155, 142, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#3e8275';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(74, 155, 142, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 155, 142, 0.3)';
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(74, 155, 142, 0.3)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(74, 155, 142, 0.4)';
              }}
            >
              Continue
            </button>
          </div>
        </div>
    </>
  );
};

export default LanguageSelectionPreview;
